import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/getFamily';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Gets family member information for a member.
 * @param {object} logger
 */
export const GetFamily = () => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Fetching member family members' data.`, { correlation: { transactionId } });

        // step 2: await Web service call
        const getFamily = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
            },
            body: JSON.stringify({
                operationName: 'GetFamilyQuery',
                variable: {},
                query: `
                    query GetFamilyQuery {
                        member {
                            familyMembers {
                                personId
                                familyMemberType
                                status
                                firstName
                                lastName
                                inSchool
                                gender
                                birthDate
                                terminationDate
                                isStudentStatusExtendable
                                specialDependent
                                benefits (federatedFilter: { status: { _equals: "ACTIVE" } }) {
                                    memberBenefitId
                                    status
                                    covered
                                    benefitName
                                    eligibleForCoverage
                                    cobRequired
                                    code
                                    cob {
                                        memberBenefitId
                                        hasOtherInsurer
                                        cobPolicyNumber
                                        cobClaimCertNumber
                                        otherInsurerName
                                    }
                                }
                            }
                            allowedDependentActions {
                                addSpouse
                                updateSpouse
                                addChild
                                updateChild
                                extendStudent
                            }
                        }
                    }
                `,
            }),
        });

        const getFamilyResponse = await getFamily.json();
        if (getFamilyResponse.errors) {
            const { message, code } = getFamilyResponse.errors[0];
            logger.critical(
                `${ACTION_NAME_PREFIX}: Failure when fetching member family members' data. Code: ${code} Reason: ${message}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }
        // step 3: Response Action
        return dispatch({ type: RESPONSE_ACTION_NAME, data: getFamilyResponse.data.member, success: true });
    };
};

export const requestReducer = produce((state) => {
    state.MemberData.isLoadingFamilyData = true;
});

export const responseReducer = produce((state, action) => {
    state.MemberData.isLoadingFamilyData = false;
    state.MemberData.familyMembers = action.data.familyMembers;
    state.MemberData.allowedDependentActions = action.data.allowedDependentActions;
});

/**
 * Fired off when the request encouters an error.
 * An error thrown by this part of the code is considered a critical failure.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isLoadingFamilyData = false;
    state.Errors.initialLoad.hasError = true;
    state.Errors.initialLoad.code = action.code;
    state.Errors.initialLoad.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
